import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { ytEmbedUrl } from "@/lib/youtube";
import { formatDateIT } from "@/lib/date";
import { absMedia } from "@/components/ExercisesTab";

/**
 * Client-side plans view: shows ONLY the currently-active plan (backend filters).
 * Renders days as tabs; each day shows its exercises with logging.
 */
export default function ClientPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/training-plans");
      setPlans(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-white/50 label-caps">Caricamento…</div>;

  if (plans.length === 0) {
    return (
      <div>
        <div className="label-caps mb-2">Le mie schede</div>
        <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mb-6">Nessuna scheda attiva</h2>
        <div className="card-surface p-12 text-center text-white/50" data-testid="no-active-plan">
          Nessuna scheda attiva al momento. Riceverai una notifica appena Sara ne caricherà una nuova.
        </div>
      </div>
    );
  }

  // Client only ever sees the single currently-active plan
  const plan = plans[0];
  return <ActivePlanView plan={plan} onChanged={load} />;
}

function ActivePlanView({ plan, onChanged }) {
  const [exercises, setExercises] = useState([]);
  const [logs, setLogs] = useState([]);
  const [lastDay, setLastDay] = useState(null);
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [openItem, setOpenItem] = useState(null);

  const loadAll = async () => {
    try {
      const [e, l, ld] = await Promise.all([
        api.get("/exercises"),
        api.get(`/training-plans/${plan.id}/logs`),
        api.get(`/training-plans/${plan.id}/last-day`),
      ]);
      setExercises(e.data); setLogs(l.data); setLastDay(ld.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [plan.id]);

  const days = plan.days || [];
  const activeDay = days[activeDayIdx] || days[0];
  const activeItems = (activeDay?.items) || [];
  const exMap = Object.fromEntries(exercises.map(e => [e.id, e]));

  return (
    <div>
      <div className="label-caps mb-1">Scheda attiva</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mb-2" data-testid="active-plan-name">{plan.name}</h2>
      {plan.description && <p className="text-white/60 mb-2">{plan.description}</p>}
      {plan.valid_until && (
        <div className="text-xs uppercase tracking-widest text-white/60 mb-2">
          Valida fino al {formatDateIT(plan.valid_until)}
        </div>
      )}
      {lastDay?.day_name && lastDay?.session_date && (
        <div className="text-[11px] uppercase tracking-widest text-[#A855F7] mb-4" data-testid="last-day-done">
          Ultimo giorno svolto: {lastDay.day_name} — {formatDateIT(lastDay.session_date)}
        </div>
      )}

      {/* Days tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="client-day-tabs">
        {days.map((d, i) => (
          <button key={d.id} data-testid={`client-day-tab-${i}`}
            onClick={() => { setActiveDayIdx(i); setOpenItem(null); }}
            className={`px-3 py-1.5 font-heading uppercase tracking-widest text-xs border ${i === activeDayIdx ? "bg-[#A855F7] border-[#A855F7] text-white" : "border-white/15 text-white/70 hover:border-[#A855F7]"}`}>
            {d.name} <span className="text-[10px] ml-2 opacity-70">({(d.items || []).length})</span>
          </button>
        ))}
      </div>

      {activeItems.length === 0 && (
        <div className="card-surface p-12 text-center text-white/40">Questo giorno non ha ancora esercizi.</div>
      )}

      <div className="space-y-3">
        {activeItems.map((it, idx) => {
          const ex = exMap[it.exercise_id];
          const itemLogs = logs.filter(l => l.item_id === it.id);
          const isOpen = openItem === it.id;
          return (
            <div key={it.id} data-testid={`item-${it.id}`} className="card-surface">
              <button onClick={() => setOpenItem(isOpen ? null : it.id)} className="w-full p-4 flex items-center gap-4 text-left">
                <div className="w-10 h-10 flex items-center justify-center border border-white/10 font-heading font-bold text-sm">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold uppercase tracking-tight text-base">{ex?.name || "—"}</div>
                  <div className="text-xs text-white/60 mt-0.5">
                    {it.sets} × {it.reps} · recupero {it.rest_seconds}s
                    {ex?.muscle_group && <span className="text-[#A855F7]"> · {ex.muscle_group}</span>}
                  </div>
                </div>
                {itemLogs.length > 0 && <div className="text-[10px] text-[#10B981] uppercase tracking-widest">{itemLogs.length} log</div>}
                {isOpen ? <ChevronUp size={16} className="text-white/60" /> : <ChevronDown size={16} className="text-white/60" />}
              </button>
              {isOpen && ex && (
                <div className="border-t border-white/5 p-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <MediaBlock exercise={ex} />
                    {ex.description && <div className="mt-3 text-xs text-white/60 whitespace-pre-line">{ex.description}</div>}
                    {it.notes && <div className="mt-3 border-l-2 border-[#A855F7] pl-3 text-xs">
                      <div className="label-caps mb-1">Note trainer</div>
                      {it.notes}
                    </div>}
                  </div>
                  <div>
                    <LogForm planId={plan.id} item={it} onSaved={loadAll} />
                    <LogHistory logs={itemLogs} onDeleted={loadAll} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MediaBlock({ exercise }) {
  const yt = ytEmbedUrl(exercise.media_url);
  const uploads = exercise.media_urls || [];
  return (
    <div className="space-y-2">
      {yt && (
        <div className="aspect-video w-full border border-white/10">
          <iframe src={yt} title={exercise.name} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      )}
      {!yt && exercise.media_url && (
        <a href={exercise.media_url} target="_blank" rel="noreferrer" className="block border border-white/10">
          <img src={exercise.media_url} alt={exercise.name} className="w-full max-h-[280px] object-cover" />
        </a>
      )}
      {uploads.length > 0 && (
        <div className={`grid gap-2 ${uploads.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid={`ex-uploads-${exercise.id}`}>
          {uploads.map((u, i) => (
            <a key={i} href={absMedia(u)} target="_blank" rel="noreferrer" className="block border border-white/10">
              <img src={absMedia(u)} alt="" className="w-full aspect-square object-cover" />
            </a>
          ))}
        </div>
      )}
      {!yt && !exercise.media_url && uploads.length === 0 && (
        <div className="aspect-video border border-white/10 flex items-center justify-center text-white/30 text-xs">Nessun media</div>
      )}
    </div>
  );
}

function LogForm({ planId, item, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const initialSets = () => Array.from({ length: item.sets }, (_, i) => ({ set_num: i + 1, weight: "", reps: "" }));
  const [date, setDate] = useState(today);
  const [sets, setSets] = useState(initialSets());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const patchSet = (idx, field, val) => setSets(sets.map((s, i) => i === idx ? { ...s, [field]: val } : s));

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/training-plans/${planId}/items/${item.id}/log`, {
        session_date: date,
        sets_data: sets.map(s => ({ set_num: s.set_num, weight: s.weight === "" ? null : Number(s.weight), reps: s.reps === "" ? null : Number(s.reps) })),
        notes,
      });
      toast.success("Log salvato — giorno completato");
      setSets(initialSets()); setNotes("");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="label-caps mb-2">Log allenamento</div>
      <div className="flex items-center gap-2 mb-3">
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none flex-1" />
      </div>
      <div className="space-y-1 mb-3">
        {sets.map((s, i) => (
          <div key={i} className="grid grid-cols-[40px_1fr_1fr] items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Set {s.set_num}</div>
            <input type="number" step="0.5" placeholder="kg" value={s.weight} onChange={e => patchSet(i, "weight", e.target.value)} data-testid={`log-w-${item.id}-${i}`}
              className="bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
            <input type="number" placeholder="rip." value={s.reps} onChange={e => patchSet(i, "reps", e.target.value)} data-testid={`log-r-${item.id}-${i}`}
              className="bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none" />
          </div>
        ))}
      </div>
      <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note personali"
        className="w-full bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none mb-3" data-testid={`log-notes-${item.id}`} />
      <button disabled={busy} onClick={save} data-testid={`log-save-${item.id}`} className="btn-primary w-full">
        {busy ? "Salvo…" : "Salva log"}
      </button>
    </div>
  );
}

function LogHistory({ logs, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  if (logs.length === 0) return null;
  const shown = expanded ? logs : logs.slice(0, 2);
  const del = async (id) => {
    if (!window.confirm("Eliminare questo log?")) return;
    try { await api.delete(`/workout-logs/${id}`); onDeleted && onDeleted(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 border-t border-white/5 pt-3">
      <div className="label-caps mb-2">Storico ({logs.length})</div>
      <div className="space-y-2">
        {shown.map(l => (
          <div key={l.id} className="border border-white/5 p-2 text-xs">
            <div className="flex items-center justify-between">
              <div className="font-heading uppercase tracking-widest text-white/80">{l.session_date}</div>
              <button onClick={() => del(l.id)} className="text-[#FF3366]/70 hover:text-[#FF3366]"><Trash2 size={11} /></button>
            </div>
            <div className="mt-1 flex flex-wrap gap-2 text-white/60">
              {(l.sets_data || []).map((s, i) => (
                <span key={i}>Set {s.set_num}: {s.weight != null ? `${s.weight}kg` : "—"} × {s.reps ?? "—"}</span>
              ))}
            </div>
            {l.notes && <div className="mt-1 text-white/50 italic">"{l.notes}"</div>}
          </div>
        ))}
      </div>
      {logs.length > 2 && (
        <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-[#A855F7] mt-2 uppercase tracking-widest">
          {expanded ? "Meno" : `Mostra tutti (${logs.length})`}
        </button>
      )}
    </div>
  );
}
